import React, { PropsWithChildren, ChangeEvent, useState, useCallback } from 'react';

import Button from '@mui/material/Button';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Grid from '@mui/material/Grid';
import { InputLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import { useParams } from 'react-router-dom';

const phoneNumberRegex = new RegExp(/^\+[0-9]+$/);

const ErrorMessage = styled(Typography)({
  color: 'red',
  display: 'flex',
  alignItems: 'center',
  margin: '1em 0 0.2em',
  '& svg': {
    marginRight: '0.4em',
  },
});

const GutterBottomTypography = styled(Typography)({
  marginBottom: '1em',
});

const PasscodeContainer = styled('div')({
  minHeight: '70px',
});

const SubmitButton = styled(Button)(({ theme }) => ({
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

interface InviteFormProps {
  apiKey: string
  onSuccess(): void;
  topic?: string;
}

export default function InviteForm({ onSuccess, apiKey, topic = "" }: PropsWithChildren<InviteFormProps>) {
  let { meetingId } = useParams<{ meetingId?: string }>();

  const [countryCode, setCountryCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');


  const [error, setError] = useState('');
  const [inProgress, setInProgress] = useState(false);

  const sendInvite = useCallback((contactNumber: string) => {

    // const api_key = window.sessionStorage.getItem('m_key');
    // const m_key = api_key ? api_key : "";

    return fetch(`${process.env.REACT_APP_API_BASE_URL}/internal/invite/number`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'X-API-KEY': apiKey
      },
      body: JSON.stringify({
        phoneNumber: contactNumber,
        meeting_id: meetingId
      }),
    }).then(async res => {
      const jsonResponse = await res.json();
      // console.log("jsonResponse", jsonResponse);

      if (!res.ok) {
        const error = new Error(jsonResponse.error?.message || 'There was an error while sending invitation.');
        error.code = jsonResponse.error?.code;

        return Promise.reject(error);
      }

      return jsonResponse;
    });
  }, [apiKey, meetingId]);

  const invite = (contactNumber: string) => {
    setError('');
    setInProgress(true);

    sendInvite(contactNumber)
      .then(() => {
        setInProgress(false);
        setCountryCode('');
        setPhoneNumber('');
        // console.log("Send-invite-success");
        onSuccess()
      })
      .catch(err => {
        console.error("err", err)
        setInProgress(false);
        setError('Unable to process invite request, Please retry again after sometime or contact Administator');
      });
  };

  const handleSubmit = () => {
    // e.preventDefault();
    let contactNumber = "+" + countryCode.trim() + phoneNumber.trim();

    if (contactNumber === "" || !phoneNumberRegex.test(contactNumber)) {
      setError("Please enter a valid phone number");
    } else {
      invite(contactNumber);
    }
  };

  return (
    <>
      <GutterBottomTypography variant="h6">
        Enter details for join meeting {topic}
      </GutterBottomTypography>

      <Grid container spacing={1}>
        <Grid size={4}>
          <PasscodeContainer>
            <InputLabel shrink htmlFor="input-countrycode">
              Country Code
            </InputLabel>
            <TextField
              id="input-countrycode"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setCountryCode(e.target.value)}
              type="text"
              variant="outlined"
              size="small"
              fullWidth={true}
              value={countryCode}
              InputProps={{
                startAdornment: <InputAdornment position="start">+</InputAdornment>,
              }}
            />
          </PasscodeContainer>
        </Grid>
        <Grid size={8}>
          <PasscodeContainer>
            <InputLabel shrink htmlFor="input-phonenumber">
              Phone Number
            </InputLabel>
            <TextField
              id="input-phonenumber"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)}
              type="text"
              variant="outlined"
              size="small"
              fullWidth={true}
              value={phoneNumber}
            />
          </PasscodeContainer>
        </Grid>
        <div>
          {error && (
            <ErrorMessage variant="caption">
              <ErrorOutlineIcon />
              {error}
            </ErrorMessage>
          )}
        </div>
      </Grid>
      <Grid container justifyContent="flex-end">
        <SubmitButton
          variant="contained"
          color="primary"
          type="button"
          disabled={inProgress || countryCode === "" || phoneNumber === ""}
          onClick={(inProgress || countryCode === "" || phoneNumber === "") ? () => { } : () => handleSubmit()}
        >
          Call Me
        </SubmitButton>
      </Grid>
    </>
  );
}
